import { makeVar, useReactiveVar } from '@apollo/client';
import deepEqual from 'react-fast-compare';

import type Currency from '../../../types/payment/Currency';
import type { Configuration } from '../../../types/Configuration';
import findDefaultCurrency from './findDefaultCurrency';
import useExperience from '../experience/useExperience';
import useAppConfig from '../config/useAppConfig';

export { default as findExperienceCurrencyCode } from './findExperienceCurrencyCode';

const initialState: Currency = {
  code: '',
  symbol: '',
  label: 'common.currency.unknown',
};

const selectedCurrency = makeVar<Currency>(initialState);

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const resetSelectedCurrency = () => {
  selectedCurrency(initialState);
};

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const setSelectedCurrency = (currency: Currency) => {
  const current = selectedCurrency();
  if (!deepEqual(current, currency)) {
    selectedCurrency(currency);
  }
};

const initializeCurrentCurrency = (config: Configuration) => {
  const initalized = selectedCurrency().code !== '';

  if (!initalized) {
    const defaultCurrency = findDefaultCurrency(config);

    setSelectedCurrency(defaultCurrency);
  }
};

const useCurrentCurrency = () => {
  const reactiveSelectedCurrency = useReactiveVar(selectedCurrency);

  const {
    selectors: { getCurrencies },
  } = useAppConfig();

  const {
    states: { experience },
  } = useExperience();

  const experienceCurrency = getCurrencies().find((currency) => currency?.code === experience?.currencyCode);

  return {
    actions: {
      setSelectedCurrency,
      initializeCurrentCurrency,
    },
    states: {
      currentCurrency: experienceCurrency ?? reactiveSelectedCurrency,
      selectedCurrency: reactiveSelectedCurrency,
    },
  };
};

export default useCurrentCurrency;
